import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

// import excelLogo from '@/assets/excel-logo.svg';
import { me } from '@connected/api-client/data/auth/me';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table';

export const Route = createFileRoute('/_auth/customer/locations/_modules/electrical/')({
    component: RouteComponent,
});

export type Object = components['schemas']['ListObjectPortalQueryResultObject'];

function RouteComponent() {
    const { t } = useTranslation('customer-locations');
    const search = Route.useSearch();

    const columns = useMemo<ColumnDef<Object>[]>(() => [
        {
            accessorKey: 'Locality',
            header: () => (
                <DataTable.Header>
                    {t('locations.lists.modules.electrical.locations')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                return (
                    <DataTable.Cell
                        variant="link"
                        to="/customer/locations/$locationId"
                        params={{ locationId: row.original.ObjectId }}
                    >
                        <div>
                            <div>
                                {row.original.Locality}
                                {' '}
                                {row.original.PostalCode}
                            </div>
                            {row.original.Street}
                            {' '}
                            {row.original.StreetNumber}
                        </div>
                    </DataTable.Cell>
                );
            },
            sortingFn: (rowA, rowB) => {
                const titleA = rowA.original.Locality ?? '';
                const titleB = rowB.original.Locality ?? '';
                return titleA.localeCompare(titleB);
            },
            sortDescFirst: false,
        },
        {
            accessorKey: 'SevereObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    column={column}
                    align="center"
                >
                    {t('locations.lists.modules.electrical.severe')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const object = row.original;

                const electricalModules = (object.Modules ?? []).filter(module =>
                    module.Kind === 'Electrical',
                );

                const severeCount = electricalModules.reduce((total, module) => {
                    const severeIssues = (module.Issues ?? []).filter(issue =>
                        issue.Kind === 'ElectricalProjectObservationSevere',
                    );
                    return total + severeIssues.reduce((sum, issue) => sum + (issue.Count ?? 0), 0);
                }, 0);

                if (severeCount === 0)
                    return null;

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('locations.lists.modules.electrical.severe'),
                            color: electricalModules[0]?.Issues?.find(issue => issue.Kind === 'ElectricalProjectObservationSevere')?.HexColorCode ?? '#df3c32',
                            children: severeCount,
                        }}
                        label={t('locations.lists.modules.electrical.severe')}
                    />
                );
            },
        },
        {
            accessorKey: 'SeriousObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    column={column}
                    align="center"
                >
                    {t('locations.lists.modules.electrical.serious')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const object = row.original;

                const electricalModules = (object.Modules ?? []).filter(module =>
                    module.Kind === 'Electrical',
                );

                const seriousCount = electricalModules.reduce((total, module) => {
                    const seriousIssues = (module.Issues ?? []).filter(issue =>
                        issue.Kind === 'ElectricalProjectObservationSerious',
                    );
                    return total + seriousIssues.reduce((sum, issue) => sum + (issue.Count ?? 0), 0);
                }, 0);

                if (seriousCount === 0)
                    return null;

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('locations.lists.modules.electrical.serious'),
                            color: electricalModules[0]?.Issues?.find(issue => issue.Kind === 'ElectricalProjectObservationSerious')?.HexColorCode ?? '#f4841b',
                            children: seriousCount,
                        }}
                        label={t('locations.lists.modules.electrical.serious')}
                    />
                );
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    column={column}
                    align="center"
                >
                    {t('locations.lists.modules.electrical.minor')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const object = row.original;

                const electricalModules = (object.Modules ?? []).filter(module =>
                    module.Kind === 'Electrical',
                );

                const minorCount = electricalModules.reduce((total, module) => {
                    const minorIssues = (module.Issues ?? []).filter(issue =>
                        issue.Kind === 'ElectricalProjectObservationMinor',
                    );
                    return total + minorIssues.reduce((sum, issue) => sum + (issue.Count ?? 0), 0);
                }, 0);

                if (minorCount === 0)
                    return null;

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('locations.lists.modules.electrical.minor'),
                            color: electricalModules[0]?.Issues?.find(issue => issue.Kind === 'ElectricalProjectObservationMinor')?.HexColorCode ?? '#e4ce0e',
                            children: minorCount,
                        }}
                        label={t('locations.lists.modules.electrical.minor')}
                    />
                );
            },
        },
        {
            accessorKey: 'RemarkObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    column={column}
                    align="center"
                >
                    {t('locations.lists.modules.electrical.remarks')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const object = row.original;

                const electricalModules = (object.Modules ?? []).filter(module =>
                    module.Kind === 'Electrical',
                );

                const remarkCount = electricalModules.reduce((total, module) => {
                    const remarkIssues = (module.Issues ?? []).filter(issue =>
                        issue.Kind === 'ElectricalProjectObservationRemark',
                    );
                    return total + remarkIssues.reduce((sum, issue) => sum + (issue.Count ?? 0), 0);
                }, 0);

                if (remarkCount === 0)
                    return null;

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('locations.lists.modules.electrical.remarks'),
                            color: electricalModules[0]?.Issues?.find(issue => issue.Kind === 'ElectricalProjectObservationRemark')?.HexColorCode ?? '#1bc0f4',
                            children: remarkCount,
                        }}
                        label={t('locations.lists.modules.electrical.remarks')}
                    />
                );
            },
        },
    ], [t]);

    const { data, isLoading } = useQuery(portaalQueryOptions.portal.objects.list({
        OrganizationId: me.useQuery().data?.Organization?.OrganizationId ?? null,
    }));

    const objectsData = useMemo(
        () => (!data || isLoading) ? { Objects: [] } : data,
        [data, isLoading],
    );

    const filteredData = search.search
        ? objectsData.Objects.filter(
                x =>
                    ((x.Locality ?? '') + (x.PostalCode ?? ''))
                        .toLowerCase()
                        .includes(search.search?.toLowerCase() || '')
                        || (x.Street ?? '').toLowerCase().includes(search.search?.toLowerCase() || ''),
            )
        : objectsData.Objects;

    return (
        <>
            <DataTable.TableHead
                title={t('locations.modules.electrical.title')}
                label={`${filteredData.length} ${t('locations.lists.locations')}`}
                buttons={[
                    // {
                    //     name: t('locations.lists.excel'),
                    //     icon: <img src={excelLogo} className="h-5 w-5" />,
                    //     callback: () => logger.debug(t('locations.lists.excel')), // TODO: change callback
                    // },
                ]}
            />
            <DataTable columns={columns} data={filteredData} />
        </>
    );
}
